<template>
    <ion-select
        class="select-simple light-grey4-background purple-purple-color"
        :class="selectClass"
        :cancel-text="cancelTextRef"
        :compareWith="compareWith"
        :disabled="disabled"
        :interface="interface"
        :interface-options="interfaceOptions"
        :mode="mode"
        :multiple="multiple"
        :name="name"
        :ok-text="okText"
        :placeholder="placeholder"
        :selected-text="selectedText"
        :value="modelValue"
        @ion-change="handlerChange"
        @ion-blur="handleBlur"
    >
        <slot name="options">
            <template v-if="parsedOptions">
                <template v-for="option of parsedOptions" :key="option.value">
                    <ion-select-option :value="option.value" :disabled="option.disabled">
                        {{ option.text }}
                    </ion-select-option>
                </template>
            </template>
        </slot>
    </ion-select>
</template>

<script setup lang="ts">
import { IonSelectOption, IonSelect, SelectCustomEvent } from "@ionic/vue";
import { ref, computed, watch } from "vue";
import { t } from "@/plugins/vue-i18n";
import { useField } from "vee-validate";

import { getOptions } from "@/functions/converter";

import { ION_MODE, ION_SELECT_INTERFACE, LABEL_POSITION } from "@/types/enum/ionicEnum";
import { SelectBaseProps } from "@/types/props";
import { SelectBaseEmits } from "@/types/emits";
import { SelectOption } from "@/types/others";

const props = withDefaults(defineProps<SelectBaseProps>(), {
    okText: t('button.ok') ?? 'Ok',
    valueKey: 'value',
    textKey: 'text',
    lines: 'none',
    position: LABEL_POSITION.STACKED,
    placeholder: 'Auswählen',
    interface: ION_SELECT_INTERFACE.ACTION_SHEET,
    interfaceOptions: () => {
        return { mode: ION_MODE.IOS }
    }
})

const emit = defineEmits<SelectBaseEmits>()

// Vee-Validate Composable
const {
    name,
    handleChange,
    handleBlur,
    validate
} = useField(
    props.validateOption?.name ?? '',
    props.validateOption?.rules ?? '',
    {
        valueProp: props.modelValue,
        initialValue: JSON.parse(JSON.stringify(props.modelValue ?? '')),
        label: props.validateOption?.label ?? props.validateOption?.name,
        ...props.validateOption?.options
    }
)

const cancelTextRef = ref<string>(props.cancelText ?? t('button.cancel'))

const parsedOptions = computed<SelectOption[]>(() => {
    if (!props.options) return []
    const textKey = props.textKey
    const valueKey = props.valueKey
    return getOptions(props.options, valueKey, textKey)
})

const handlerChange = (event: SelectCustomEvent): void => {
    emit('update:modelValue', event.detail.value);
    handleChange(event);
    emit('change', event.detail.value)
}

watch(props, () => {
    if (props.modelValue) handleChange(props.modelValue)
})

window.addEventListener('change-language', () => {
    validate({ mode: 'force' })
})
</script>

<style scoped>
.select-simple {
    --border-radius: 8px;
    border-radius: 8px;
    --padding-start: 10px;
    min-height: 38px;
    --padding-top: 0;
    max-width: 500px;
}
</style>
